import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Github, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/i18n/LanguageContext";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectsPage = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTech, setSelectedTech] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedTech) {
      setFilteredProjects(
        projects.filter((p) =>
          p.technologies?.some((t) =>
            t.toLowerCase().includes(selectedTech.toLowerCase())
          )
        )
      );
    } else {
      setFilteredProjects(projects);
    }
  }, [selectedTech, projects]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
        setFilteredProjects(data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get all unique technologies
  const allTechnologies = [
    ...new Set(projects.flatMap((p) => p.technologies || [])),
  ].sort();

  return (
    <>
      <SEO
        title="Projets"
        description="Découvrez mes projets de développement web et mobile. Applications, sites web, et solutions techniques réalisées."
        tags={["projets", "développement", "web", "mobile", "applications"]}
      />
      <div className="min-h-screen pt-16 md:pt-20 pb-12 md:pb-16" data-testid="projects-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8 md:py-12"
        >
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
            {t("projects.title")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto px-4">
            {t("projects.subtitle")}
          </p>
        </motion.div>

        {allTechnologies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 md:mb-12 px-2"
          >
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-4xl mx-auto">
              <Button
                variant={selectedTech === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTech("")}
                className="rounded-full text-xs sm:text-sm h-7 sm:h-8 px-3 sm:px-4"
              >
                {t("projects.filterAll")}
              </Button>
              {allTechnologies.slice(0, 12).map((tech) => (
                <Button
                  key={tech}
                  variant={selectedTech === tech ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedTech(tech)}
                  className="rounded-full text-xs sm:text-sm h-7 sm:h-8 px-3 sm:px-4"
                >
                  {tech}
                </Button>
              ))}
              {allTechnologies.length > 12 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs sm:text-sm h-7 sm:h-8 px-3 sm:px-4 text-muted-foreground"
                >
                  +{allTechnologies.length - 12}
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-all duration-300"
                data-testid={`project-${project.id}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {project.project_of_month && (
                      <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                        ⭐ {t("projects.projectOfMonth")}
                      </Badge>
                    )}
                    {project.featured && !project.project_of_month && (
                      <Badge className="bg-primary text-primary-foreground">
                        {t("blog.featured")}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.technologies?.slice(0, 4).map((tech) => (
                      <Badge
                        key={tech}
                        variant="secondary"
                        className="text-xs"
                      >
                        {tech}
                      </Badge>
                    ))}
                    {project.technologies?.length > 4 && (
                      <Badge variant="secondary" className="text-xs">
                        +{project.technologies.length - 4}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {project.demo_url && (
                      <a
                        href={project.demo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button size="sm" className="rounded-full gap-1.5">
                          <ExternalLink className="h-3.5 w-3.5" />
                          {t("projects.viewDemo")}
                        </Button>
                      </a>
                    )}
                    {project.github_url && (
                      <a
                        href={project.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full gap-1.5"
                        >
                          <Github className="h-3.5 w-3.5" />
                          {t("projects.viewCode")}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Filter className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              {t("projects.noResults")}
            </h3>
            <p className="text-muted-foreground">
              {selectedTech ? t("projects.noTech") : t("projects.comingSoon")}
            </p>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProjectsPage;
